import { type ReactNode } from 'react'
import { actionLabel, entityLabel, type AuditRow } from './types'
import { fmtDateTime, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { changelogMessages } from '@/i18n/messages/changelog'

function prettyDetail(detail: string | null): string | null {
  if (!detail) return null
  try {
    return JSON.stringify(JSON.parse(detail), null, 2)
  } catch {
    return detail
  }
}

/** Read-only detail of one logged change, in the app's standard modal chrome. */
export function ChangeLogDetailDialog({ row, onClose }: { row: AuditRow; onClose: () => void }) {
  const t = useT(changelogMessages)
  const tc = useT(commonMessages)
  const when = fmtDateTime(row.occurredAt)
  const detail = prettyDetail(row.detail)

  const rows: { label: string; value: ReactNode }[] = [
    { label: t('colWhen'), value: when },
    { label: t('colWho'), value: row.actorName ?? row.actorId },
    { label: t('colAction'), value: actionLabel(row.action) },
    { label: t('colWhat'), value: entityLabel(row.entityType) },
    { label: t('colItem'), value: <code className="text-xs">{row.entityId}</code> },
    ...(row.field ? [{ label: t('colField'), value: row.field }] : []),
  ]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[min(88vh,100dvh-2rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{t('detailTitle')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">{when}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={tc('close')}
            className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
            {rows.map((r) => (
              <div key={r.label} className="contents">
                <dt className="text-neutral-500">{r.label}</dt>
                <dd className="font-medium">{r.value}</dd>
              </div>
            ))}
          </dl>

          {(row.oldValue !== null || row.newValue !== null) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-red-50 p-3 ring-1 ring-red-100 dark:bg-red-500/10 dark:ring-red-500/20">
                <div className="mb-1 text-xs font-medium text-red-700 dark:text-red-300">{t('before')}</div>
                <div className="whitespace-pre-wrap break-words text-sm">
                  {row.oldValue ?? <span className="text-neutral-400">{t('emptyValue')}</span>}
                </div>
              </div>
              <div className="rounded-xl bg-emerald-50 p-3 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:ring-emerald-500/20">
                <div className="mb-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">{t('after')}</div>
                <div className="whitespace-pre-wrap break-words text-sm">
                  {row.newValue ?? <span className="text-neutral-400">{t('emptyValue')}</span>}
                </div>
              </div>
            </div>
          )}

          {detail && (
            <div>
              <div className="mb-1 text-xs font-medium text-neutral-500">{t('extra')}</div>
              <pre className="overflow-x-auto rounded-xl bg-neutral-50 p-3 text-xs ring-1 ring-neutral-200 dark:bg-neutral-800/50 dark:ring-neutral-700">
                {detail}
              </pre>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-neutral-200 p-5 dark:border-neutral-800">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800"
          >
            {tc('close')}
          </button>
        </div>
      </div>
    </div>
  )
}
